import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { EnvelopeSimple } from "@phosphor-icons/react";
import { EASE_OUT, MaskWords, PageFrame, Seo } from "../../kit.jsx";
import { ArrowLink, Kicker, clamp01 } from "./shared.jsx";
import "./about.css";

const SECTIONS = [
  {
    id: "gv-people",
    title: "Who runs GRID",
    body: (
      <>
        <p>GRID is run by school students. Moksh Sindhwani (CEO) and Tanishq Aggarwal (CTO) co-founded it in December 2025. Harsh Jain (CCO) and Yash Varshney (CGO) complete the founding board.</p>
        <p>The team directory names the person responsible for each area: programmes, partnerships, engineering, outreach, community and design.</p>
        <ArrowLink to="/team">View the team</ArrowLink>
      </>
    ),
  },
  {
    id: "gv-legal",
    title: "Legal status",
    body: (
      <>
        <p>Project GRID is in registration as a Section 8 non-profit. Until that is complete, GRID is a student-led initiative, not a registered charity or company.</p>
      </>
    ),
  },
  {
    id: "gv-credit",
    title: "Programme attribution",
    body: (
      <>
        <p>Every programme page says who led it and what GRID did. GRID organised AAROH with Tachyon India and CiPD, IIIT Delhi, and runs Aurora. Metamorphosis was organised by Tale of Humankind Foundation and IIT Delhi's outreach office, with GRID as the outreach partner.</p>
        <p>A meeting is not an endorsement or a partnership.</p>
        <div className="ab-gv__links">
          <ArrowLink to="/programmes">Our programmes</ArrowLink>
          <ArrowLink to="/partnerships">How we credit partners</ArrowLink>
          <ArrowLink to="/meetings">People we've met</ArrowLink>
        </div>
      </>
    ),
  },
  {
    id: "gv-safety",
    title: "Youth safety",
    body: (
      <>
        <p>Programme participation should use clear consent, supervision and communication practices.</p>
        <p>Send any safety concern to <a href="mailto:founder@projectgrid.org?subject=Safety%20concern">founder@projectgrid.org</a>.</p>
      </>
    ),
  },
  {
    id: "gv-privacy",
    title: "Privacy",
    body: (
      <>
        <p>We collect only the information needed to respond to you, deliver a programme or keep you informed.</p>
        <p>Photos of meetings and events come from public posts or event archives. If a photo of you needs to come down, email us with the page address.</p>
        <ArrowLink to="/privacy">Privacy policy</ArrowLink>
      </>
    ),
  },
  {
    id: "gv-access",
    title: "Access",
    body: (
      <>
        <p>We design for different devices, internet speeds, abilities and levels of experience. Aurora was built so students with slow internet could still take part.</p>
      </>
    ),
  },
  {
    id: "gv-contact",
    title: "Questions, concerns and corrections",
    body: (
      <>
        <p>If a name, photograph, credit or figure on this site is wrong, or you have a concern about how GRID is run, email us. Include the page address if it is about something on the site.</p>
        <a className="ab-gv__mail" href="mailto:founder@projectgrid.org?subject=Governance%20question">
          <EnvelopeSimple weight="bold" aria-hidden="true" />
          founder@projectgrid.org
        </a>
      </>
    ),
  },
];

function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    let frame = 0;
    const check = () => {
      frame = 0;
      const line = window.innerHeight * 0.35;
      let current = ids[0];
      ids.forEach((id) => {
        const element = document.getElementById(id);
        if (element && element.getBoundingClientRect().top <= line) current = id;
      });
      setActive((previous) => (previous === current ? previous : current));
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(check);
    };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [ids]);
  return active;
}

const IDS = SECTIONS.map((section) => section.id);

export default function GovernancePage() {
  const reduceMotion = useReducedMotion();
  const active = useActiveSection(IDS);
  const { scrollYProgress } = useScroll();
  const progress = useTransform(scrollYProgress, (value) => clamp01(value * 1.15));
  const appear = (delay) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: EASE_OUT },
  });

  return (
    <PageFrame className="rd-page ab-page ab-page--governance">
      <Seo
        title="Governance"
        path="/governance"
        description="Project GRID's public operating principles for youth participation, attribution, privacy and safeguarding."
      />
      <main>
        <section className="ab-ghero" aria-labelledby="ab-ghero-title">
          <div className="ab-ghero__copy">
            <motion.div {...appear(0)}><Kicker>How we're run</Kicker></motion.div>
            <MaskWords as="h1" id="ab-ghero-title" className="ab-display ab-display--mid" text="How GRID is run, and who to ask." onMount delay={0.08} />
            <motion.p className="ab-lede" {...appear(0.4)}>
              GRID is a youth-led initiative. This page sets out who is responsible and the rules we follow.
            </motion.p>
          </div>
          <motion.aside className="ab-ghero__contact" aria-label="Raise a concern" {...appear(0.5)}>
            <span className="ab-kicker">Raise a concern</span>
            <a href="mailto:founder@projectgrid.org?subject=Governance%20question">founder@projectgrid.org</a>
            <p>For corrections, safety concerns or questions about how GRID works.</p>
            <dl>
              <div><dt>Status</dt><dd>In registration as a Section 8 non-profit</dd></div>
              <div><dt>Page updated</dt><dd>September 2026</dd></div>
            </dl>
          </motion.aside>
        </section>

        <div className="ab-gv">
          <nav className="ab-gv__toc" aria-label="On this page">
            <span className="ab-kicker">On this page</span>
            <div className="ab-gv__toc-inner">
              <span className="ab-gv__track" aria-hidden="true">
                <motion.span style={reduceMotion ? { scaleY: 1 } : { scaleY: progress }} />
              </span>
              <ol>
                {SECTIONS.map((section, index) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`} aria-current={active === section.id ? "location" : undefined} className={active === section.id ? "is-active" : undefined}>
                      {active === section.id && (
                        <motion.span
                          className="ab-gv__marker"
                          layoutId="ab-gv-marker"
                          transition={{ duration: reduceMotion ? 0 : 0.45, ease: EASE_OUT }}
                          aria-hidden="true"
                        />
                      )}
                      <span className="ab-gv__n">{String(index + 1).padStart(2, "0")}</span>
                      <span>{section.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </nav>
          <div className="ab-gv__doc">
            {SECTIONS.map((section, index) => (
              <section key={section.id} id={section.id} className="ab-gv__sec" aria-labelledby={`${section.id}-t`}>
                <span className="ab-gv__big" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                <h2 id={`${section.id}-t`}>{section.title}</h2>
                <div className="ab-gv__text">{section.body}</div>
              </section>
            ))}
            <p className="ab-gv__foot">
              Related: <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link> · <Link to="/contact">Contact</Link>
            </p>
          </div>
        </div>
      </main>
    </PageFrame>
  );
}
